<script setup lang="ts">
import { computed, h } from 'vue'

import { useRoute, useRouter } from 'vue-router'

import { Icon } from '@iconify/vue'

import SVGIcon from '@/components/UI/SVGIcon.vue'

import { useCheckPlanFeatures } from '@/composables/useCheckPlanFeature'
import { useShorterUrlDialog } from '@/composables/useShorterUrlDialog'

import { ShorterDialogMode } from '@/constant/client/shorterDialog'
import appStore from '@/stores'

import type { NavItem } from '@/types/navbar'

const route = useRoute()
const router = useRouter()

const shortUrlStore = appStore.useShortUrlStore
const { openShorterUrlDialog } = useShorterUrlDialog()

const { canBatchCreateShortUrls } = useCheckPlanFeatures()

const navLinks = computed<NavItem[]>(() => {
  return [
    {
      name: '網址列表',
      routeName: 'shorter-list',
      queryParams: { companyUuid: shortUrlStore.selectedCompanyUuId, orgId: shortUrlStore.selectedOrganizationId },
      disabled: false,
      icon: () => h(Icon, { icon: 'ri:link', width: '20px', height: '20px' })
    },
    {
      name: '群組列表',
      routeName: 'group-list',
      queryParams: { companyUuid: shortUrlStore.selectedCompanyUuId, orgId: shortUrlStore.selectedOrganizationId },
      disabled: !canBatchCreateShortUrls.value,
      icon: () => h(SVGIcon, { name: 'group-url-link-icon', width: '30px', height: '20px' })
    },
    {
      name: '受眾標籤',
      routeName: 'tag-stats',
      queryParams: { companyUuid: shortUrlStore.selectedCompanyUuId, orgId: shortUrlStore.selectedOrganizationId },
      disabled: false,
      icon: () => h(Icon, { icon: 'ri:group-line', width: '20px', height: '20px' })
    }
  ]
})

const navigateTo = (item: NavItem) => {
  if (item.disabled) {
    return
  }
  router.push({
    name: item.routeName,
    query: item.queryParams
  })
}

const openDialog = () => {
  openShorterUrlDialog({
    mode: ShorterDialogMode.SINGLE_CREATE
  })
}
</script>
<template>
  <aside class="w-16 shrink-0 bg-app-primary-blue-10 py-6 pl-2 md:w-[84px] md:pl-9">
    <el-tooltip :hide-after="0" placement="right" content="建立短網址">
      <button type="button" class="add-short-url-btn" @click="openDialog">
        <Icon icon="ri:add-line" width="20px" height="20px" class="text-white" />
      </button>
    </el-tooltip>
    <ul class="flex flex-col gap-4">
      <li v-for="item in navLinks" :key="item.routeName">
        <el-tooltip :hide-after="0" placement="right" :content="item.name">
          <button
            type="button"
            class="sidebar-link"
            :class="{
              active: item.routeName === route.name,
              disabled: item.disabled
            }"
            :disabled="item.disabled"
            @click="navigateTo(item)"
          >
            <component :is="item.icon" />
          </button>
        </el-tooltip>
      </li>
    </ul>
  </aside>
</template>

<style scoped>
.add-short-url-btn {
  @apply mb-4 flex h-12 w-12 items-center justify-center rounded-full border-none bg-app-primary-blue-350 transition-all;
  box-shadow: 0 0 8.133px 0 rgba(255, 255, 255, 0.25) inset;
  backdrop-filter: blur(16.399999618530273px);
}

.add-short-url-btn:hover {
  background:
    linear-gradient(0deg, rgba(24, 25, 27, 0.1) 0%, rgba(24, 25, 27, 0.1) 100%), rgb(var(--color-primary-blue-350));
}

.sidebar-link {
  @apply flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border-none bg-white text-app-black-400 transition-all;
  box-shadow:
    0 0 8.133px 0 rgba(255, 255, 255, 0.25) inset,
    0 0 8.133px 0 rgba(255, 255, 255, 0.25) inset;
  backdrop-filter: blur(16.399999618530273px);
}

.sidebar-link:hover:not(.disabled):not(.active) {
  @apply text-app-black-600 transition-colors;
}

.sidebar-link.active {
  @apply bg-[#adb1b8] text-white;
}

.sidebar-link.disabled {
  @apply cursor-not-allowed opacity-50;
}
</style>
